/**
 * The interpreter's in-call Tools: which sections a call shows, and in what
 * order. Shared by the web panel (components/web/InterpreterToolsPanel.tsx)
 * and the Terp sheet (vrs-layout/components/InterpreterCallTools.native.tsx),
 * so both read the same way: Phone, Transfer, Team, Add participant, Shift.
 */
export const INTERPRETER_TOOL_SECTIONS = [
    'phone',
    'transfer',
    'team',
    'addParticipant',
    'shift'
];
/**
 * Whether the hearing-party phone belongs to this call: VRS calls only. A
 * live phone leg always keeps its section, so it can be ended.
 *
 * @param {IInterpreterToolsContext} context - The call.
 * @returns {boolean}
 */
export function phoneApplies(context) {
    return Boolean(context.phoneLive) || (context.callType !== 'vri' && !context.vriRoom);
}
/**
 * The sections this call shows, in order. Transfer and Add participant need
 * the queue call they act on; Team shows when teaming applies (team.ts). A
 * teammate sees Team and Shift only.
 *
 * @param {IInterpreterToolsContext} context - The call.
 * @returns {InterpreterToolSection[]}
 */
export function interpreterToolSections(context) {
    if (context.teammate) {
        return INTERPRETER_TOOL_SECTIONS.filter(section => section === 'shift' || (section === 'team' && Boolean(context.callId)));
    }
    return INTERPRETER_TOOL_SECTIONS.filter(section => {
        switch (section) {
            case 'phone':
                return phoneApplies(context);
            case 'transfer':
            case 'addParticipant':
                return Boolean(context.callId);
            case 'team':
                return Boolean(context.callId && context.teamApplies);
            default:
                return true;
        }
    });
}
/**
 * Whether a phone leg is live: it needs the Phone section and shows on the key.
 *
 * @param {IPhoneLineState} phone - The voice service's call status.
 * @returns {boolean}
 */
export function isPhoneLive(phone) {
    return Boolean(phone.hangupPending) || ['dialing', 'ringing', 'connected'].includes(phone.status);
}
/**
 * What the minimized Tools key shows: live state only (a phone leg with its
 * timer, a transfer waiting for acceptance), nothing when all is quiet.
 *
 * @param {IPhoneLineState} phone - The voice service's call status.
 * @param {string} duration - The voice service's formatted call time.
 * @param {boolean} transferPending - A transfer is waiting for acceptance.
 * @returns {IToolsLiveState}
 */
export function toolsLiveState(phone, duration, transferPending) {
    const live = { transferPending };
    if (phone.hangupPending) {
        live.phone = 'endUnconfirmed';
    }
    else if (phone.status === 'connected' || phone.status === 'dialing' || phone.status === 'ringing') {
        live.phone = phone.status;
        if (phone.status === 'connected' && duration) {
            live.timer = duration;
        }
    }
    return live;
}
/**
 * Whether the key has anything live to show.
 *
 * @param {IToolsLiveState} live - The key's live state.
 * @returns {boolean}
 */
export function hasLiveState(live) {
    return Boolean(live.phone) || live.transferPending;
}
